import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Upload, Save, X, Image } from 'lucide-react';
import IconPicker, { ICONS } from './IconPicker';
import MediaPickerModal from './MediaPickerModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CoursesManagerEnhanced() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [editingCourse, setEditingCourse] = useState(null);
  const [editingChapter, setEditingChapter] = useState(null);
  const [editingContent, setEditingContent] = useState(null);
  const [showCourseForm, setShowCourseForm] = useState(false);
  const [showChapterForm, setShowChapterForm] = useState(false);
  const [showContentForm, setShowContentForm] = useState(false);
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [testCategories, setTestCategories] = useState([]); // Load test categories
  
  // File upload states
  const [uploadingFile, setUploadingFile] = useState(false);
  const [showUnsplashPicker, setShowUnsplashPicker] = useState(false);
  
  const [courseForm, setCourseForm] = useState({
    title: '',
    description: '',
    author: '',
    date: new Date().getFullYear().toString(),
    price: 0,
    is_free: true,
    cover_image_url: '',
    preview_description: '',
    tier: 'basic', // basic, comps_ready, advanced
    category: '', // Test category slug
    duration_hours: 1,
    display_icon: 'Heart'
  });
  
  const [chapterForm, setChapterForm] = useState({
    title: '',
    description: '',
    order: 1,
    icon: 'Brain'
  });
  
  const [contentForm, setContentForm] = useState({
    type: 'text',
    title: '',
    content_url: '',
    slide_urls: [],
    content_text: '',
    quiz_id: '',
    question_ids: [],
    order: 1,
    is_preview: false
  });

  // Additional states for quiz management
  const [availableQuizzes, setAvailableQuizzes] = useState([]);
  const [availableQuestions, setAvailableQuestions] = useState([]);
  const [selectedQuestions, setSelectedQuestions] = useState([]);

  useEffect(() => {
    loadCourses();
    loadQuizzes();
    loadQuestions();
    loadTestCategories();
  }, []);

  const loadCourses = async () => {
    try {
      const response = await axios.get(`${API}/admin/courses`, { withCredentials: true });
      setCourses(response.data);
    } catch (error) {
      toast.error('Failed to load courses');
    }
  };

  const loadTestCategories = async () => {
    try {
      const response = await axios.get(`${API}/test-categories`);
      setTestCategories(response.data.filter(cat => cat.enabled));
    } catch (error) {
      console.error('Failed to load test categories');
    }
  };


  const loadQuizzes = async () => {
    try {
      const response = await axios.get(`${API}/admin/tests`, { withCredentials: true });
      setAvailableQuizzes(response.data);
    } catch (error) {
      console.error('Failed to load quizzes');
    }
  };

  const loadQuestions = async () => {
    try {
      const response = await axios.get(`${API}/admin/question-pools`, { withCredentials: true });
      setAvailableQuestions(response.data);
    } catch (error) {
      console.error('Failed to load questions');
    }
  };


  const loadCourseDetails = async (courseId) => {
    try {
      const response = await axios.get(`${API}/courses/${courseId}`);
      setSelectedCourse(response.data);
    } catch (error) {
      toast.error('Failed to load course details');
    }
  };

  // File Upload Handlers
  const handleFileUpload = async (file, type) => {
    if (!file) return null;
    
    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      let endpoint = '';
      switch (type) {
        case 'cover':
          endpoint = '/admin/courses/upload-cover';
          break;
        case 'video':
          endpoint = '/admin/courses/upload-video';
          break;
        case 'audio':
          endpoint = '/admin/courses/upload-audio';
          break;
        case 'slides':
          endpoint = '/admin/courses/upload-slides';
          break;
        default:
          throw new Error('Invalid upload type');
      }
      
      const response = await axios.post(`${API}${endpoint}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });
      
      toast.success('File uploaded successfully!');
      return response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload file');
      return null;
    } finally {
      setUploadingFile(false);
    }
  };

  const handleCoverUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const url = await handleFileUpload(file, 'cover');
      if (url) {
        if (editingCourse) {
          setEditingCourse({ ...editingCourse, cover_image_url: url });
        } else {
          setCourseForm({ ...courseForm, cover_image_url: url });
        }
      }
    }
  };


  const handleUnsplashSelect = (url) => {
    if (editingCourse) {
      setEditingCourse({ ...editingCourse, cover_image_url: url });
    } else {
      setCourseForm({ ...courseForm, cover_image_url: url });
    }
  };


  const handleContentFileUpload = async (e, type) => {
    const file = e.target.files[0];
    if (file) {
      const url = await handleFileUpload(file, type);
      if (url) {
        if (editingContent) {
          setEditingContent({ ...editingContent, content_url: url });
        } else {
          setContentForm({ ...contentForm, content_url: url });
        }
      }
    }
  };

  const handleMultipleSlidesUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 30) {
      toast.error('Maximum 30 slides allowed');
      return;
    }
    
    setUploadingFile(true);
    try {
      const formData = new FormData();
      files.forEach(file => {
        formData.append('files', file);
      });
      
      const response = await axios.post(`${API}/admin/courses/upload-multiple-slides`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });
      
      toast.success(`${response.data.count} slides uploaded successfully!`);
      
      if (editingContent) {
        setEditingContent({ ...editingContent, slide_urls: response.data.urls });
      } else {
        setContentForm({ ...contentForm, slide_urls: response.data.urls });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload slides');
    } finally {
      setUploadingFile(false);
    }
  };

  // Course CRUD
  const handleCreateCourse = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/courses`, courseForm, {
        withCredentials: true
      });
      toast.success('Course created!');
      setShowCourseForm(false);
      resetCourseForm();
      loadCourses();
    } catch (error) {
      toast.error('Failed to create course');
    }
  };

  const handleUpdateCourse = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/admin/courses/${editingCourse.id}`, editingCourse, {
        withCredentials: true
      });
      toast.success('Course updated!');
      setEditingCourse(null);
      if (selectedCourse?.id === editingCourse.id) {
        setSelectedCourse(editingCourse);
      }
      loadCourses();
    } catch (error) {
      // Better error handling - extract meaningful message from Pydantic validation errors
      let errorMessage = 'Failed to update course';
      
      if (error.response?.data) {
        const data = error.response.data;
        if (data.detail) {
          errorMessage = typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail);
        } else if (Array.isArray(data)) {
          // Pydantic validation errors format
          errorMessage = data.map(e => `${e.loc?.join('.') || 'field'}: ${e.msg}`).join(', ');
        } else if (typeof data === 'string') {
          errorMessage = data;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
      console.error('Update course error:', error.response?.data || error);
    }
  };

  const handlePublishCourse = async (courseId, currentStatus) => {
    const newStatus = currentStatus === 'published' ? 'draft' : 'published';
    const action = newStatus === 'published' ? 'publish' : 'unpublish';
    
    try {
      // Get current course data from admin endpoint to ensure we have all fields
      const response = await axios.get(`${API}/courses/${courseId}`, {
        withCredentials: true
      });
      const courseData = response.data;
      
      // Update status and is_published fields
      courseData.status = newStatus;
      courseData.is_published = newStatus === 'published';
      
      // Make sure we have all required fields with defaults if needed
      if (!courseData.title) courseData.title = 'Untitled Course';
      if (!courseData.description) courseData.description = '';
      if (!courseData.author) courseData.author = 'AcuErnie';
      if (!courseData.date) courseData.date = new Date().getFullYear().toString();
      if (courseData.price === undefined) courseData.price = 0.0;
      if (courseData.is_free === undefined) courseData.is_free = true;
      
      await axios.put(`${API}/admin/courses/${courseId}`, courseData, {
        withCredentials: true
      });
      
      toast.success(`Course ${action}ed successfully!`);
      loadCourses();
      if (selectedCourse?.id === courseId) {
        setSelectedCourse({...selectedCourse, status: newStatus, is_published: newStatus === 'published'});
      }
    } catch (error) {
      // Better error handling - extract meaningful message from Pydantic validation errors
      let errorMessage = `Failed to ${action} course`;
      
      if (error.response?.data) {
        const data = error.response.data;
        if (data.detail) {
          errorMessage = typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail);
        } else if (Array.isArray(data)) {
          // Pydantic validation errors format
          errorMessage = data.map(e => `${e.loc?.join('.') || 'field'}: ${e.msg}`).join(', ');
        } else if (typeof data === 'string') {
          errorMessage = data;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
      console.error(`${action} course error:`, error.response?.data || error);
    }
  };

  const handleDeleteCourse = async (courseId) => {
    if (!window.confirm('Delete this course? This will also delete all chapters and content.')) return;
    try {
      await axios.delete(`${API}/admin/courses/${courseId}`, {
        withCredentials: true
      });
      toast.success('Course deleted');
      setSelectedCourse(null);
      loadCourses();
    } catch (error) {
      toast.error('Failed to delete course');
    }
  };

  // Chapter CRUD
  const handleCreateChapter = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/courses/${selectedCourse.id}/chapters`, chapterForm, {
        withCredentials: true
      });
      toast.success('Chapter added!');
      setShowChapterForm(false);
      resetChapterForm();
      loadCourseDetails(selectedCourse.id);
    } catch (error) {
      toast.error('Failed to add chapter');
    }
  };

  const handleUpdateChapter = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/admin/chapters/${editingChapter.id}`, editingChapter, {
        withCredentials: true
      });
      toast.success('Chapter updated!');
      setEditingChapter(null);
      loadCourseDetails(selectedCourse.id);
    } catch (error) {
      toast.error('Failed to update chapter');
    }
  };

  const handleDeleteChapter = async (chapterId) => {
    if (!window.confirm('Delete this chapter and all its content?')) return;
    try {
      await axios.delete(`${API}/admin/chapters/${chapterId}`, {
        withCredentials: true
      });
      toast.success('Chapter deleted');
      loadCourseDetails(selectedCourse.id);
    } catch (error) {
      toast.error('Failed to delete chapter');
    }
  };

  // Content CRUD
  const handleCreateContent = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/chapters/${selectedChapter.id}/content`, contentForm, {
        withCredentials: true
      });
      toast.success('Content added!');
      setShowContentForm(false);
      resetContentForm();
      loadCourseDetails(selectedCourse.id);
    } catch (error) {
      toast.error('Failed to add content');
    }
  };

  const handleUpdateContent = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/admin/content/${editingContent.id}`, editingContent, {
        withCredentials: true
      });
      toast.success('Content updated!');
      setEditingContent(null);
      loadCourseDetails(selectedCourse.id);
    } catch (error) {
      toast.error('Failed to update content');
    }
  };

  const handleDeleteContent = async (contentId) => {
    if (!window.confirm('Delete this content?')) return;
    try {
      await axios.delete(`${API}/admin/content/${contentId}`, {
        withCredentials: true
      });
      toast.success('Content deleted');
      loadCourseDetails(selectedCourse.id);
    } catch (error) {
      toast.error('Failed to delete content');
    }
  };

  const resetCourseForm = () => {
    setCourseForm({
      title: '', description: '', author: '', date: new Date().getFullYear().toString(),
      price: 0, is_free: true, cover_image_url: '', preview_description: '',
      difficulty: 'beginner', duration_hours: 1
    });
  };

  const resetChapterForm = () => {
    setChapterForm({ title: '', description: '', order: 1, icon: 'Brain' });
  };

  const resetContentForm = () => {
    setContentForm({ type: 'text', title: '', content_url: '', slide_urls: [], content_text: '', order: 1, is_preview: false });
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl retro-title">Courses Management</h2>
        <button
          onClick={() => navigate('/admin/courses/new')}
          className="retro-button flex items-center space-x-2"
        >
          <Plus className="w-4 h-4" />
          <span>New Course</span>
        </button>
      </div>

      {/* Course List - Modern Design */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {courses.map((course) => (
          <div 
            key={course.id} 
            className="group relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-[#E8934D]/20"
          >
            {/* Course Cover with Gradient Overlay */}
            <div className="relative h-48 bg-gradient-to-br from-[#E85D4D] via-[#E8934D] to-[#4D9E7C] overflow-hidden">
              {course.cover_image_url ? (
                <>
                  <img
                    src={`${BACKEND_URL}${course.cover_image_url}`}
                    alt={course.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                </>
              ) : course.display_icon ? (
                <div className="w-full h-full flex items-center justify-center">
                  {(() => {
                    const iconData = ICONS.find(i => i.name === course.display_icon);
                    const IconComponent = iconData?.icon;
                    console.log('Course:', course.title, 'Icon:', course.display_icon, 'Found:', !!IconComponent);
                    return IconComponent ? (
                      <IconComponent className="w-24 h-24 text-white opacity-80" strokeWidth={1.5} />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <div className="text-white text-6xl opacity-30">📚</div>
                      </div>
                    );
                  })()}
                </div>
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <div className="text-white text-6xl opacity-30">📚</div>
                </div>
              )}
              
              {/* Difficulty Badge */}
              <div className="absolute top-3 right-3 flex gap-2">
                {/* Status Badge */}
                <span className={`px-3 py-1 ${
                  course.status === 'published' ? 'bg-green-500' : 
                  course.status === 'draft' ? 'bg-gray-500' : 
                  'bg-yellow-500'
                } text-white rounded-full text-xs font-bold shadow-lg uppercase`}>
                  {course.status || 'draft'}
                </span>
                <span className="px-3 py-1 bg-white/90 backdrop-blur-sm rounded-full text-xs font-bold text-[#2C1810] shadow-lg">
                  {course.difficulty || 'Beginner'}
                </span>
              </div>
              
              {/* Free/Paid Badge */}
              <div className="absolute top-3 left-3">
                <span className={`px-3 py-1 ${course.is_free ? 'bg-green-500' : 'bg-[#E8934D]'} text-white rounded-full text-xs font-bold shadow-lg`}>
                  {course.is_free ? 'FREE' : `$${course.price}`}
                </span>
              </div>
            </div>

            {/* Course Info */}
            <div className="p-6">
              <h3 className="text-xl font-bold text-[#2C1810] mb-2 line-clamp-1 group-hover:text-[#E85D4D] transition-colors">
                {course.title}
              </h3>
              <p className="text-sm text-[#6B4423] mb-4 line-clamp-2 leading-relaxed">
                {course.description}
              </p>
              
              {/* Course Meta */}
              <div className="flex items-center gap-4 text-xs text-[#6B4423] mb-4 pb-4 border-b border-[#E8934D]/20">
                <span className="flex items-center gap-1">
                  ⏱️ {course.duration_hours || 1}h
                </span>
                <span className="flex items-center gap-1">
                  👤 {course.author || 'AcuErnie'}
                </span>
              </div>
              
              {/* Action Buttons */}
              <div className="flex gap-2">
                <button
                  onClick={() => loadCourseDetails(course.id)}
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-[#E85D4D] to-[#E8934D] text-white rounded-lg font-bold hover:shadow-lg transition-all"
                >
                  Manage
                </button>
                <button
                  onClick={() => handlePublishCourse(course.id, course.status)}
                  className={`px-3 py-2 ${
                    course.status === 'published' 
                      ? 'bg-gray-500 hover:bg-gray-600' 
                      : 'bg-green-500 hover:bg-green-600'
                  } text-white rounded-lg transition-colors font-medium text-sm`}
                  title={course.status === 'published' ? 'Unpublish' : 'Publish'}
                >
                  {course.status === 'published' ? '📦' : '🚀'}
                </button>
                <button
                  onClick={() => navigate(`/admin/courses/${course.id}/edit`)}
                  className="p-2 bg-[#4D9E7C] text-white rounded-lg hover:bg-[#E8934D] transition-colors"
                  title="Edit"
                >
                  <Edit className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDeleteCourse(course.id)}
                  className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                  title="Delete"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Course Form (Create/Edit) */}
      {(showCourseForm || editingCourse) && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="cassette-card p-6 max-w-5xl w-full my-8">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-2xl font-bold text-[#2C1810]">
                {editingCourse ? 'Edit Course' : 'Create New Course'}
              </h3>
              <button
                onClick={() => {
                  setShowCourseForm(false);
                  setEditingCourse(null);
                }}
                className="text-[#2C1810]"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <form onSubmit={editingCourse ? handleUpdateCourse : handleCreateCourse} className="space-y-4">
              <div>
                <label className="block text-[#2C1810] font-bold mb-2">Title *</label>
                <input
                  type="text"
                  value={editingCourse ? editingCourse.title : courseForm.title}
                  onChange={(e) =>
                    editingCourse
                      ? setEditingCourse({ ...editingCourse, title: e.target.value })
                      : setCourseForm({ ...courseForm, title: e.target.value })
                  }
                  required
                  className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-white text-[#2C1810]"
                />
              </div>

              <div>
                <label className="block text-[#2C1810] font-bold mb-2">Description *</label>
                <textarea
                  value={editingCourse ? editingCourse.description : courseForm.description}
                  onChange={(e) =>
                    editingCourse
                      ? setEditingCourse({ ...editingCourse, description: e.target.value })
                      : setCourseForm({ ...courseForm, description: e.target.value })
                  }
                  required
                  rows="3"
                  className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-white text-[#2C1810]"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[#2C1810] font-bold mb-2">Author *</label>
                  <input
                    type="text"
                    value={editingCourse ? editingCourse.author : courseForm.author}
                    onChange={(e) =>
                      editingCourse
                        ? setEditingCourse({ ...editingCourse, author: e.target.value })
                        : setCourseForm({ ...courseForm, author: e.target.value })
                    }
                    required
                    className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-white text-[#2C1810]"
                  />
                </div>

                <div>
                  <label className="block text-[#2C1810] font-bold mb-2">Year</label>
                  <input
                    type="text"
                    value={editingCourse ? editingCourse.date : courseForm.date}
                    onChange={(e) =>
                      editingCourse
                        ? setEditingCourse({ ...editingCourse, date: e.target.value })
                        : setCourseForm({ ...courseForm, date: e.target.value })
                    }
                    className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-white text-[#2C1810]"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[#2C1810] font-bold mb-2">Cover Image</label>
                <button
                  type="button"
                  onClick={() => setShowUnsplashPicker(true)}
                  className="retro-button bg-blue-600 text-white hover:bg-blue-700 flex items-center gap-2"
                >
                  <Image className="w-4 h-4" />
                  Choose Image
                </button>
                
                {/* Show preview if image is selected */}
                {(editingCourse ? editingCourse.cover_image_url : courseForm.cover_image_url) && (
                  <div className="mt-3">
                    <img
                      src={editingCourse ? editingCourse.cover_image_url : courseForm.cover_image_url}
                      alt="Cover preview"
                      className="h-32 w-48 object-cover rounded border-2 border-[#6B4423]"
                    />
                    <p className="text-xs text-[#6B4423] mt-1">Selected cover image</p>
                  </div>
                )}
              </div>
              
              {/* Icon Picker */}
              <IconPicker
                selectedIcon={editingCourse ? editingCourse.display_icon : courseForm.display_icon}
                onSelectIcon={(icon) =>
                  editingCourse
                    ? setEditingCourse({ ...editingCourse, display_icon: icon })
                    : setCourseForm({ ...courseForm, display_icon: icon })
                }
              />

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[#2C1810] font-bold mb-2">Tier</label>
                  <select
                    value={editingCourse ? editingCourse.tier || 'basic' : courseForm.tier}
                    onChange={(e) => {
                      const newTier = e.target.value;
                      if (editingCourse) {
                        setEditingCourse({ ...editingCourse, tier: newTier, category: '' });
                      } else {
                        setCourseForm({ ...courseForm, tier: newTier, category: '' });
                      }
                    }}
                    required
                    className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-white text-[#2C1810]"
                  >
                    <option value="basic">Basic Practice</option>
                    <option value="comps_ready">Comp's Ready</option>
                    <option value="advanced">Advanced</option>
                  </select>
                </div>

                <div>
                  <label className="block text-[#2C1810] font-bold mb-2">Category</label>
                  <select
                    value={editingCourse ? editingCourse.category || '' : courseForm.category}
                    onChange={(e) =>
                      editingCourse
                        ? setEditingCourse({ ...editingCourse, category: e.target.value })
                        : setCourseForm({ ...courseForm, category: e.target.value })
                    }
                    required
                    className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-white text-[#2C1810]"
                  >
                    <option value="">Select Category</option>
                    {testCategories
                      .filter(cat => cat.tier === (editingCourse ? editingCourse.tier || 'basic' : courseForm.tier))
                      .map(cat => (
                        <option key={cat.slug} value={cat.slug}>{cat.name}</option>
                      ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-[#2C1810] font-bold mb-2">Duration (hours)</label>
                <input
                  type="number"
                  step="0.5"
                  value={editingCourse ? editingCourse.duration_hours : courseForm.duration_hours}
                  onChange={(e) =>
                    editingCourse
                      ? setEditingCourse({ ...editingCourse, duration_hours: parseFloat(e.target.value) })
                      : setCourseForm({ ...courseForm, duration_hours: parseFloat(e.target.value) })
                  }
                  className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-white text-[#2C1810]"
                />
              </div>

              <div>
                <label className="block text-[#2C1810] font-bold mb-2">
                  <input
                    type="checkbox"
                    checked={editingCourse ? editingCourse.is_free : courseForm.is_free}
                    onChange={(e) =>
                      editingCourse
                        ? setEditingCourse({ ...editingCourse, is_free: e.target.checked })
                        : setCourseForm({ ...courseForm, is_free: e.target.checked })
                    }
                    className="mr-2"
                  />
                  Free Course
                </label>
              </div>

              {!(editingCourse ? editingCourse.is_free : courseForm.is_free) && (
                <div>
                  <label className="block text-[#2C1810] font-bold mb-2">Price ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editingCourse ? editingCourse.price : courseForm.price}
                    onChange={(e) =>
                      editingCourse
                        ? setEditingCourse({ ...editingCourse, price: parseFloat(e.target.value) })
                        : setCourseForm({ ...courseForm, price: parseFloat(e.target.value) })
                    }
                    className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-white text-[#2C1810]"
                  />
                </div>
              )}

              <button
                type="submit"
                disabled={uploadingFile}
                className="retro-button w-full"
              >
                {uploadingFile ? 'Uploading...' : editingCourse ? 'Update Course' : 'Create Course'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Course Details Management */}
      {selectedCourse && !editingCourse && (
        <div className="cassette-card p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-2xl font-bold text-[#2C1810]">{selectedCourse.title}</h3>
            <button
              onClick={() => setSelectedCourse(null)}
              className="retro-button-small"
            >
              Back to Courses
            </button>
          </div>

          {/* Chapters Section */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-4">
              <h4 className="text-xl font-bold text-[#2C1810]">Chapters</h4>
              <button
                onClick={() => {
                  resetChapterForm();
                  setShowChapterForm(true);
                  setEditingChapter(null);
                }}
                className="retro-button-small flex items-center space-x-2"
              >
                <Plus className="w-4 h-4" />
                <span>Add Chapter</span>
              </button>
            </div>

            {selectedCourse.chapters?.map((chapter) => (
              <div 
                key={chapter.id} 
                className="mb-6 p-6 bg-gradient-to-br from-white to-[#F4E4C1]/30 rounded-xl border-2 border-[#E8934D]/30 shadow-md hover:shadow-lg transition-all"
              >
                {editingChapter?.id === chapter.id ? (
                  <form onSubmit={handleUpdateChapter} className="space-y-3">
                    <input
                      type="text"
                      value={editingChapter.title}
                      onChange={(e) => setEditingChapter({ ...editingChapter, title: e.target.value })}
                      className="w-full px-3 py-2 border-2 border-[#6B4423] rounded bg-white text-[#2C1810]"
                      required
                    />
                    <textarea
                      value={editingChapter.description}
                      onChange={(e) => setEditingChapter({ ...editingChapter, description: e.target.value })}
                      className="w-full px-3 py-2 border-2 border-[#6B4423] rounded bg-white text-[#2C1810]"
                      rows="2"
                    />
                    
                    {/* Icon Picker for Editing Chapter */}
                    <IconPicker
                      selectedIcon={editingChapter.icon || 'Brain'}
                      onSelectIcon={(icon) => setEditingChapter({ ...editingChapter, icon })}
                    />
                    
                    <div className="flex gap-2">
                      <button type="submit" className="retro-button-small">
                        <Save className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditingChapter(null)}
                        className="retro-button-small bg-gray-400"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </form>
                ) : (
                  <>
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="flex items-center justify-center w-10 h-10 bg-gradient-to-br from-[#E85D4D] to-[#E8934D] text-white font-bold rounded-lg text-lg">
                            {chapter.order}
                          </span>
                          <h5 className="text-xl font-bold text-[#2C1810]">
                            {chapter.title}
                          </h5>
                        </div>
                        {chapter.description && (
                          <p className="text-sm text-[#6B4423] ml-13 leading-relaxed">{chapter.description}</p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setSelectedChapter(chapter);
                            resetContentForm();
                            setShowContentForm(true);
                          }}
                          className="p-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                          title="Add Content"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setEditingChapter(chapter)}
                          className="p-2 bg-[#4D9E7C] text-white rounded-lg hover:bg-[#E8934D] transition-colors"
                          title="Edit Chapter"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteChapter(chapter.id)}
                          className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                          title="Delete Chapter"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {/* Chapter Content - Modern Design */}
                    <div className="space-y-2">
                      {chapter.contents?.map((content, idx) => (
                        <div 
                          key={content.id} 
                          className="group p-4 bg-white rounded-lg border border-[#E8934D]/20 hover:border-[#E8934D] hover:shadow-md transition-all"
                        >
                          {editingContent?.id === content.id ? (
                          <ContentForm
                            content={editingContent}
                            setContent={setEditingContent}
                            onSubmit={handleUpdateContent}
                            onCancel={() => setEditingContent(null)}
                            onFileUpload={handleContentFileUpload}
                            onMultipleSlidesUpload={handleMultipleSlidesUpload}
                            uploadingFile={uploadingFile}
                            availableQuizzes={availableQuizzes}
                          />
                        ) : (
                          <div className="flex justify-between items-center">
                            <div className="flex items-center gap-3 flex-1">
                              <span className="flex items-center justify-center w-8 h-8 bg-[#E8934D]/20 text-[#E85D4D] font-bold rounded-lg text-sm">
                                {idx + 1}
                              </span>
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="font-semibold text-[#2C1810]">{content.title}</span>
                                  <span className={`text-xs px-2 py-1 rounded-full font-bold ${
                                    content.type === 'video' ? 'bg-purple-100 text-purple-700' :
                                    content.type === 'audio' ? 'bg-blue-100 text-blue-700' :
                                    content.type === 'quiz' ? 'bg-green-100 text-green-700' :
                                    content.type === 'essay' ? 'bg-orange-100 text-orange-700' :
                                    content.type === 'chapter' ? 'bg-indigo-100 text-indigo-700' :
                                    'bg-gray-100 text-gray-700'
                                  }`}>
                                    {content.type === 'quiz' ? '🎯 Quiz' :
                                     content.type === 'essay' ? '✍️ Essay' :
                                     content.type === 'chapter' ? '📚 Chapter' :
                                     content.type.toUpperCase()}
                                  </span>
                                  {content.is_preview && (
                                    <span className="text-xs bg-green-500 text-white px-2 py-1 rounded-full font-bold">
                                      👁️ Preview
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => setEditingContent(content)}
                                className="p-2 text-[#4D9E7C] hover:bg-[#4D9E7C] hover:text-white rounded-lg transition-all"
                                title="Edit"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteContent(content.id)}
                                className="p-2 text-red-500 hover:bg-red-500 hover:text-white rounded-lg transition-all"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>

          {/* Chapter Form Modal */}
          {showChapterForm && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="cassette-card p-6 max-w-3xl w-full">
                <h4 className="text-xl font-bold text-[#2C1810] mb-4">Add New Chapter</h4>
                <form onSubmit={handleCreateChapter} className="space-y-4">
                  <div>
                    <label className="block text-[#2C1810] font-bold mb-2">Title *</label>
                    <input
                      type="text"
                      value={chapterForm.title}
                      onChange={(e) => setChapterForm({ ...chapterForm, title: e.target.value })}
                      required
                      className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-white text-[#2C1810]"
                    />
                  </div>
                  <div>
                    <label className="block text-[#2C1810] font-bold mb-2">Description</label>
                    <textarea
                      value={chapterForm.description}
                      onChange={(e) => setChapterForm({ ...chapterForm, description: e.target.value })}
                      rows="3"
                      className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-white text-[#2C1810]"
                    />
                  </div>
                  <div>
                    <label className="block text-[#2C1810] font-bold mb-2">Order</label>
                    <input
                      type="number"
                      value={chapterForm.order}
                      onChange={(e) => setChapterForm({ ...chapterForm, order: parseInt(e.target.value) })}
                      required
                      className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-white text-[#2C1810]"
                    />
                  </div>
                  
                  {/* Icon Picker for Chapter */}
                  <IconPicker
                    selectedIcon={chapterForm.icon}
                    onSelectIcon={(icon) => setChapterForm({ ...chapterForm, icon })}
                  />
                  
                  <div className="flex gap-2">
                    <button type="submit" className="retro-button flex-1">
                      Add Chapter
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowChapterForm(false)}
                      className="retro-button bg-gray-400"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Content Form Modal */}
          {showContentForm && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
              <div className="cassette-card p-6 max-w-4xl w-full my-8">
                <h4 className="text-xl font-bold text-[#2C1810] mb-4">Add Content to Chapter</h4>
                <ContentForm
                  content={contentForm}
                  setContent={setContentForm}
                  onSubmit={handleCreateContent}
                  onCancel={() => setShowContentForm(false)}
                  onFileUpload={handleContentFileUpload}
                  onMultipleSlidesUpload={handleMultipleSlidesUpload}
                  uploadingFile={uploadingFile}
                  availableQuizzes={availableQuizzes}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Unsplash Image Picker Modal */}
      <MediaPickerModal
        isOpen={showUnsplashPicker}
        onClose={() => setShowUnsplashPicker(false)}
        onSelect={handleUnsplashSelect}
      />
    </div>
  );
}

// Content Form Component
function ContentForm({ content, setContent, onSubmit, onCancel, onFileUpload, onMultipleSlidesUpload, uploadingFile, availableQuizzes }) {
  const [uploadMethod, setUploadMethod] = useState('upload');
  const [slideUploadType, setSlideUploadType] = useState('single'); // 'single' or 'multiple'

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div>
        <label className="block text-[#2C1810] font-bold mb-2">Content Type *</label>
        <select
          value={content.type}
          onChange={(e) => setContent({ ...content, type: e.target.value })}
          className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-white text-[#2C1810]"
          required
        >
          <option value="text">Text / Reading</option>
          <option value="video">Video</option>
          <option value="audio">Audio</option>
          <option value="slides">Slideshow (Multiple Images)</option>
          <option value="chapter">Chapter / Section</option>
          <option value="quiz">Quiz (From Test Bank)</option>
          <option value="essay">Essay Assignment</option>
        </select>
      </div>

      <div>
        <label className="block text-[#2C1810] font-bold mb-2">Title *</label>
        <input
          type="text"
          value={content.title}
          onChange={(e) => setContent({ ...content, title: e.target.value })}
          required
          className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-white text-[#2C1810]"
        />
      </div>

      {content.type === 'text' ? (
        <div>
          <label className="block text-[#2C1810] font-bold mb-2">Text Content *</label>
          <textarea
            value={content.content_text || ''}
            onChange={(e) => setContent({ ...content, content_text: e.target.value })}
            rows="8"
            className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-white text-[#2C1810]"
          />
        </div>
      ) : content.type === 'chapter' ? (
        <div>
          <label className="block text-[#2C1810] font-bold mb-2">Chapter Content *</label>
          <div className="bg-purple-50 border-2 border-purple-300 rounded p-3 mb-3">
            <p className="text-sm text-purple-900 font-semibold">📚 Add rich text content for a chapter or section</p>
          </div>
          <textarea
            value={content.content_text || ''}
            onChange={(e) => setContent({ ...content, content_text: e.target.value })}
            rows="10"
            placeholder="Enter chapter content, instructions, or introduction..."
            className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-white text-[#2C1810]"
          />
        </div>
      ) : content.type === 'quiz' ? (
        <div>
          <label className="block text-[#2C1810] font-bold mb-2">Select Quiz from Test Bank *</label>
          <div className="bg-green-50 border-2 border-green-300 rounded p-3 mb-3">
            <p className="text-sm text-green-900 font-semibold">🎯 Choose an existing quiz/test to add to this course</p>
          </div>
          <select
            value={content.quiz_id || ''}
            onChange={(e) => setContent({ ...content, quiz_id: e.target.value })}
            className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-white text-[#2C1810]"
            required
          >
            <option value="">-- Select a Quiz --</option>
            {availableQuizzes.map(quiz => (
              <option key={quiz.id} value={quiz.id}>
                {quiz.title} ({quiz.questions?.length || 0} questions)
              </option>
            ))}
          </select>
          <p className="text-xs text-[#6B4423] mt-2">💡 Manage quizzes in the Testing Center → Test Bank section</p>
        </div>
      ) : content.type === 'essay' ? (
        <div>
          <label className="block text-[#2C1810] font-bold mb-2">Essay Prompt / Instructions *</label>
          <div className="bg-orange-50 border-2 border-orange-300 rounded p-3 mb-3">
            <p className="text-sm text-orange-900 font-semibold">✍️ Provide instructions or prompt for the essay assignment</p>
          </div>
          <textarea
            value={content.content_text || ''}
            onChange={(e) => setContent({ ...content, content_text: e.target.value })}
            rows="8"
            placeholder="Enter essay prompt, requirements, word count, grading criteria..."
            className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-white text-[#2C1810]"
            required
          />
        </div>
      ) : content.type === 'slides' ? (
        <div>
          <label className="block text-[#2C1810] font-bold mb-2">Slideshow Images (up to 30)</label>
          <div className="bg-blue-50 border-2 border-blue-300 rounded p-3 mb-3">
            <p className="text-sm text-blue-900 font-semibold">📊 Upload multiple slide images to create a slideshow presentation</p>
          </div>
          
          {uploadMethod === 'upload' ? (
            <div>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={onMultipleSlidesUpload}
                disabled={uploadingFile}
                className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-white text-[#2C1810]"
              />
              <p className="text-xs text-[#6B4423] mt-1">Select multiple images (PNG, JPG, WebP) - Maximum 30 slides</p>
            </div>
          ) : (
            <div>
              <textarea
                placeholder="Paste image URLs, one per line"
                value={content.slide_urls?.join('\n') || ''}
                onChange={(e) => setContent({ ...content, slide_urls: e.target.value.split('\n').filter(url => url.trim()) })}
                rows="6"
                className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-white text-[#2C1810]"
              />
              <p className="text-xs text-[#6B4423] mt-1">One URL per line</p>
            </div>
          )}
          
          {content.slide_urls && content.slide_urls.length > 0 && (
            <div className="mt-3 p-3 bg-green-50 rounded border border-green-300">
              <p className="text-sm text-green-900 font-semibold">✓ {content.slide_urls.length} slides uploaded</p>
              <div className="grid grid-cols-4 gap-2 mt-2">
                {content.slide_urls.slice(0, 8).map((url, idx) => (
                  <img
                    key={idx}
                    src={url.startsWith('http') ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`}
                    alt={`Slide ${idx + 1}`}
                    className="w-full h-16 object-cover rounded border border-gray-300"
                  />
                ))}
              </div>
              {content.slide_urls.length > 8 && (
                <p className="text-xs text-gray-600 mt-2">...and {content.slide_urls.length - 8} more</p>
              )}
            </div>
          )}
        </div>
      ) : (
        <div>
          <label className="block text-[#2C1810] font-bold mb-2">
            {content.type === 'video' && 'Video File'}
            {content.type === 'audio' && 'Audio File'}
          </label>
          <div className="flex gap-2 mb-2">
            <button
              type="button"
              onClick={() => setUploadMethod('upload')}
              className={`retro-button-small ${uploadMethod === 'upload' ? 'bg-[#E8934D] text-white' : ''}`}
            >
              📤 Upload File
            </button>
            <button
              type="button"
              onClick={() => setUploadMethod('url')}
              className={`retro-button-small ${uploadMethod === 'url' ? 'bg-[#E8934D] text-white' : ''}`}
            >
              🔗 Use URL
            </button>
          </div>

          {uploadMethod === 'upload' ? (
            <input
              type="file"
              accept={
                content.type === 'video' ? 'video/*' :
                content.type === 'audio' ? 'audio/*' :
                '*'
              }
              onChange={(e) => onFileUpload(e, content.type)}
              disabled={uploadingFile}
              className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-white text-[#2C1810]"
            />
          ) : (
            <input
              type="url"
              placeholder="https://example.com/file.mp4"
              value={content.content_url || ''}
              onChange={(e) => setContent({ ...content, content_url: e.target.value })}
              className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-white text-[#2C1810]"
            />
          )}
          {content.content_url && (
            <p className="text-xs text-green-700 mt-1">✓ File uploaded: {content.content_url}</p>
          )}
        </div>
      )}

      <div>
        <label className="block text-[#2C1810] font-bold mb-2">Order</label>
        <input
          type="number"
          value={content.order}
          onChange={(e) => setContent({ ...content, order: parseInt(e.target.value) })}
          className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-white text-[#2C1810]"
        />
      </div>

      <div>
        <label className="block text-[#2C1810] font-bold">
          <input
            type="checkbox"
            checked={content.is_preview}
            onChange={(e) => setContent({ ...content, is_preview: e.target.checked })}
            className="mr-2"
          />
          Available as Preview (non-enrolled users can view)
        </label>
      </div>

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={uploadingFile}
          className="retro-button flex-1"
        >
          {uploadingFile ? 'Uploading...' : 'Save Content'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="retro-button bg-gray-400"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
